import { Injectable, inject } from "@angular/core";

import { Actions, createEffect, ofType } from "@ngrx/effects";

import { of } from 'rxjs';
import { map, mergeMap, catchError } from 'rxjs/operators';

import { LabelPrinterActions } from "@core/state/label-printers/label-printers.actions";
import { DeviceDataService, deviceToLabelPrinter } from "@core/services/datasources/device-data.service";
import { createNormalizedError } from "@core/utilities/error-handler.util";

@Injectable({ providedIn: 'root' })
export class LabelPrintersEffects {

  private readonly actions$ = inject(Actions);
  private readonly deviceDataService = inject(DeviceDataService);

  load$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LabelPrinterActions.load),
      mergeMap(() => this.deviceDataService.getAllLabelPrinters()
        .pipe(
          map(labelPrinters => LabelPrinterActions.loadSuccess({ labelPrinters })),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(LabelPrinterActions.loadFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  get$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LabelPrinterActions.get),
      map((action) => action.printerId),
      mergeMap((printerId) => this.deviceDataService.getLabelPrinter(printerId)
        .pipe(
          map(labelPrinter => LabelPrinterActions.getSuccess({ labelPrinter })),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(LabelPrinterActions.getFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  add$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LabelPrinterActions.add),
      map((action) => action.command),
      mergeMap((command) => this.deviceDataService.addLabelPrinter(command)
        .pipe(
          map((device) => deviceToLabelPrinter(device)),
          map(labelPrinter => LabelPrinterActions.addSuccess({ labelPrinter })),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(LabelPrinterActions.addFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  remove$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LabelPrinterActions.remove),
      map((action) => action.command),
      mergeMap((command) => this.deviceDataService.deleteDevice(command.id)
        .pipe(
          map(() => LabelPrinterActions.removeSuccess({ id: command.id })),
          catchError(error => {
            const normalizedError = createNormalizedError(error);
            return of(LabelPrinterActions.removeFailure({ error: normalizedError }));
          })
        )
      )
    )
  });

  update$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LabelPrinterActions.update),
      map((action) => ({ id: action.id, command: action.command })),
      mergeMap(({ id, command }) =>
        this.deviceDataService.updateLabelPrinter(id, command).pipe(
          map((labelPrinter) => LabelPrinterActions.updateSuccess({ labelPrinter })),
          catchError((error) => {
            const normalizedError = createNormalizedError(error);
            return of(LabelPrinterActions.updateFailure({ error: normalizedError }));
          })
        )
      )
    );
  });

}
